import {
    collection,
    CollectionReference,
    deleteDoc,
    deleteField,
    doc,
    getDocs,
    getDoc,
    query,
    setDoc,
    updateDoc,
    where,
    writeBatch,
} from "firebase/firestore";
import firebaseService from "./firebaseService";
import {IDateCardTemplate} from "../types/DateCardTemplate";

const CONFIG_COLLECTION = 'DateCardTemplateConfig';
const CONFIG_DOC = 'categories';

class DateCardTemplateService {
    ref: CollectionReference;
    private _categoriesPromise: Promise<string[]>;

    constructor() {
        this.ref = collection(firebaseService.db, 'DateCardTemplates');
        this._categoriesPromise = this._fetchCategories();
    }

    private _fetchCategories(): Promise<string[]> {
        const configRef = doc(firebaseService.db, CONFIG_COLLECTION, CONFIG_DOC);
        return getDoc(configRef).then((snapshot) => {
            if (!snapshot.exists()) return [];
            return (snapshot.data()?.order as string[]) ?? [];
        });
    }

    async getAll(): Promise<IDateCardTemplate[]> {
        const snapshot = await getDocs(this.ref);
        return snapshot.docs
            .filter((d) => d.exists())
            .map((d) => ({...d.data(), id: d.id} as IDateCardTemplate));
    }

    async getCategories(): Promise<string[]> {
        return this._categoriesPromise.catch(() => {
            this._categoriesPromise = this._fetchCategories();
            return this._categoriesPromise;
        });
    }

    async create(
        template: IDateCardTemplate,
        isNewCategory: boolean,
    ): Promise<void> {
        const groupSnap = await getDocs(
            query(this.ref, where('category', '==', template.category), where('gender', '==', template.gender))
        );
        const order = groupSnap.size;

        const newDoc = doc(this.ref);
        const {id, ...data} = template;
        await setDoc(newDoc, {...data, order});

        if (isNewCategory) {
            const existing = await this.getCategories();
            if (!existing.includes(template.category)) {
                await this.updateCategoryOrder([...existing, template.category]);
            }
        }
    }

    async delete(id: string): Promise<void> {
        await deleteDoc(doc(this.ref, id));
    }

    async updateCategoryOrder(order: string[]): Promise<void> {
        const configRef = doc(firebaseService.db, CONFIG_COLLECTION, CONFIG_DOC);
        await setDoc(configRef, {order}, {merge: true});
        this._categoriesPromise = Promise.resolve(order);
    }

    async updateTemplateOrders(updates: {id: string; order: number}[]): Promise<void> {
        await Promise.all(
            updates.map(({id, order}) =>
                setDoc(doc(this.ref, id), {order}, {merge: true})
            )
        );
    }

    async linkVariants(parentCardId: string, variantIds: string[]): Promise<void> {
        await Promise.all(
            variantIds.map((id) => setDoc(doc(this.ref, id), {parentCardId}, {merge: true}))
        );
    }

    async unlinkVariant(id: string): Promise<void> {
        await updateDoc(doc(this.ref, id), {parentCardId: deleteField()});
    }

    async updateThumbFields(id: string, thumb: string, thumbURL: string): Promise<void> {
        await setDoc(doc(this.ref, id), {thumb, thumbURL}, {merge: true});
    }

    async updatePaidFlag(id: string, isPaid: boolean): Promise<void> {
        await setDoc(doc(this.ref, id), {isPaid}, {merge: true});
    }

    async updatePaidFlagForGroup(mainId: string, variantIds: string[], isPaid: boolean): Promise<void> {
        const batch = writeBatch(firebaseService.db);
        batch.set(doc(this.ref, mainId), {isPaid}, {merge: true});
        for (const id of variantIds) {
            batch.set(doc(this.ref, id), {isPaid}, {merge: true});
        }
        await batch.commit();
    }

    async promoteVariantToMain(
        currentMainId: string,
        newMainId: string,
        currentMainOrder: number,
        otherVariantIds: string[]
    ): Promise<void> {
        const batch = writeBatch(firebaseService.db);
        batch.update(doc(this.ref, newMainId), {order: currentMainOrder, parentCardId: deleteField()});
        batch.set(doc(this.ref, currentMainId), {parentCardId: newMainId, order: 0}, {merge: true});
        for (const id of otherVariantIds) {
            batch.set(doc(this.ref, id), {parentCardId: newMainId}, {merge: true});
        }
        await batch.commit();
    }
}

export default new DateCardTemplateService();
